// --- React and React Native Core imports ---
import React from 'react';
import { useParams } from 'react-router-dom';

// --- 3rd party libraries ---
import { gql, useQuery } from '@apollo/client';

// --- Our Own imports (absolute) ---
import { v3 } from '@feedflo/tlv';

// --- Relative path imports (./) ---
import Heading from '../../atoms/Heading';
import {
  extractAccelerometerData,
  extractAccelerometerBlockData,
  generateWavBlob,
  downloadBlob,
} from '../../utils/wavGenerator';

import './TLVPage.scss';

const MOTOR_CURRENT_FIELDS = [
  { key: 'vibrateMotor1Current', label: 'Vibrate motor 1 (mA)' },
  { key: 'vibrateMotor2Current', label: 'Vibrate motor 2 (mA)' },
  { key: 'vibrateMotor3Current', label: 'Vibrate motor 3 (mA)' },
  { key: 'vibrateMotor4Current', label: 'Vibrate motor 4 (mA)' },
  { key: 'slideMotor1Current', label: 'Slide motor 1 (mA)' },
  { key: 'slideMotor2Current', label: 'Slide motor 2 (mA)' },
  { key: 'slideMotor3Current', label: 'Slide motor 3 (mA)' },
  { key: 'slideMotor4Current', label: 'Slide motor 4 (mA)' },
];

function signalBlocksFromTlvJson(tlvJson) {
  if (Array.isArray(tlvJson?.signalDataBlocks) && tlvJson.signalDataBlocks.length > 0) {
    return tlvJson.signalDataBlocks;
  }
  if (tlvJson?.uuid != null || tlvJson?.startTime != null) {
    return [tlvJson];
  }
  return [];
}

function motorSeriesList(block, key) {
  const value = block?.[key];
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function MotorCurrentDebugSection({ tlvJson }) {
  const blocks = signalBlocksFromTlvJson(tlvJson);
  if (blocks.length === 0) {
    return null;
  }

  return (
    <div className="motor-current-debug-section">
      <h3>Motor current captures</h3>
      {blocks.map((block, blockIndex) => {
        const hasAnyMotor = MOTOR_CURRENT_FIELDS.some(
          ({ key }) =>
            motorSeriesList(block, key).some(
              (series) => Array.isArray(series?.int16Data) && series.int16Data.length > 0,
            ),
        );
        return (
          <div key={blockIndex} className="motor-current-block">
            <h4>Signal block {blockIndex + 1}</h4>
            {!hasAnyMotor && block.startTime != null ? (
              <p>
                <b>Heartbeat:</b> block envelope only (no motor samples). start={String(block.startTime)}{' '}
                end={String(block.endTime)}
              </p>
            ) : null}
            {MOTOR_CURRENT_FIELDS.flatMap(({ key, label }) =>
              motorSeriesList(block, key).flatMap((series, captureIndex) => {
                const samples = series?.int16Data;
                if (!Array.isArray(samples) || samples.length === 0) {
                  return [];
                }
                const preview = samples.slice(0, 12).join(', ');
                const rate = series?.currentSampleRateHz ?? series?.sampleRateHz;
                const captureLabel =
                  motorSeriesList(block, key).length > 1 ? ` (capture ${captureIndex + 1})` : '';
                return [
                  <p key={`${key}-${captureIndex}`}>
                    <b>
                      {label}
                      {captureLabel}:
                    </b>{' '}
                    {samples.length} samples
                    {rate != null ? ` @ ${rate} Hz` : ''}
                    {' — '}
                    [{preview}
                    {samples.length > 12 ? ', …' : ''}]
                  </p>,
                ];
              }),
            )}
          </div>
        );
      })}
    </div>
  );
}

const DEVICE_DATA_GQL = gql`
  query DeviceDataQuery($dataId: uuid!) {
    device_data(where: { id: { _eq: $dataId } }, order_by: { started_at: asc }) {
      id
      started_at
      ended_at
      data
      device {
        serial
        device_assignments(where: { deleted_at: { _is_null: true }, status: { _eq: "active" } }) {
          feed_line {
            name
            farm {
              name
            }
          }
        }
      }
    }
  }
`;

export default function NotFoundPage() {
  const { dataId } = useParams();
  const { loading, error, data } = useQuery(DEVICE_DATA_GQL, {
    variables: {
      dataId,
    },
  });

  if (loading) return <span>Loading</span>;
  if (error) return <span>{JSON.stringify(error, null, 2)}</span>;

  return (
    <div className="TLVPage">
      <Heading text="TLV Data" />
      <p>
        <b>Device Serial : </b>
        {data.device_data[0]?.device?.serial || 'unknown'}
      </p>
      <p>
        <b>Location : </b>
        {data.device_data[0]?.device?.device_assignments[0]?.feed_line?.farm?.name || 'unknown'} {' | '}
        {data.device_data[0]?.device?.device_assignments[0]?.feed_line?.name || 'unknown'}
      </p>
      {}

      <>
        {data.device_data.map((device_data, index) => {
          const hex = device_data.data.substring(2);
          const arrayBuffer = new ArrayBuffer(hex.length / 2);
          const dataView = new DataView(arrayBuffer);
          for (let c = 0; c < hex.length; c += 2) {
            const byte = parseInt(hex.substring(c, c + 2), 16);
            dataView.setUint8(c / 2, byte);
          }

          const tlvJson = v3.TLVtoJSON(dataView);
          const accelData = extractAccelerometerData(tlvJson);
          const accelerometerRawArray = tlvJson.accelerometerRawArray || [];
          const motorBlocks = signalBlocksFromTlvJson(tlvJson);

          const jsonReplacer = (k, v) => {
            if (k === 'accelerometerRawArray') {
              return v;
            }
            if (v instanceof DataView) {
              return `DataView(${v.byteLength} bytes)`;
            }
            if (v instanceof ArrayBuffer) {
              return `ArrayBuffer(${v.byteLength} bytes)`;
            }
            if (v instanceof Array) {
              return JSON.stringify(v);
            }
            return v;
          };

          const handleDownloadWav = (block, blockIndex) => {
            const blockData = extractAccelerometerBlockData(block);
            if (blockData.xRaw.length === 0 && blockData.yRaw.length === 0 && blockData.zRaw.length === 0) {
              alert('No accelerometer raw data found in this block');
              return;
            }

            // Assume 8 bits if int8Data is present, otherwise assume precision is the final precision
            // In the future, the TLV should include the sampled-precision and final precision separately.
            const inputBitsPerSample = block?.xRaw?.int8Data ? 8 : block?.precision;
            const sampleRate = blockData.sampleRate || 44100; // default to standard audio sample rate
            const wavBlob = generateWavBlob(
              blockData.xRaw,
              blockData.yRaw,
              blockData.zRaw,
              sampleRate,
              inputBitsPerSample,
            );
            const timestamp = blockData.startTime ? `_${blockData.startTime}` : '';
            const filename = `accelerometer_${device_data.id}_block${blockIndex}${timestamp}_${Date.now()}.wav`;
            downloadBlob(wavBlob, filename);
          };

          return (
            <div key={device_data.id || index} className="tlv-data-block">
              <b>TLV Buffer:</b>
              <pre>{device_data.data}</pre>
              <b>TLV JSON Data:</b>
              <pre>
                {JSON.stringify(tlvJson, jsonReplacer, 2)}
              </pre>
              {motorBlocks.length > 0 && motorBlocks.some((block) =>
                MOTOR_CURRENT_FIELDS.some(
                  ({ key }) => Array.isArray(block?.[key]?.int16Data) && block[key].int16Data.length > 0,
                ),
              ) ? null : (
                tlvJson.unknownTags?.length > 0 && (
                  <p className="no-accelerometer-data">
                    Motor current tags are in the buffer but were not decoded (TLV library may need @feedflo/tlv
                    1.4.0+). Device API still stores the full binary payload.
                  </p>
                )
              )}
              {accelerometerRawArray.length > 0 ? (
                <div className="wav-download-section">
                  <h3>Accelerometer Data Blocks ({accelerometerRawArray.length})</h3>
                  {accelerometerRawArray.map((block, blockIndex) => {
                    const blockData = extractAccelerometerBlockData(block);
                    const hasData = blockData.xRaw.length > 0 || blockData.yRaw.length > 0 || blockData.zRaw.length > 0;
                    return (
                      <div key={blockIndex} className="accelerometer-block">
                        <h4>Block {blockIndex + 1}</h4>
                        {blockData.startTime && <p>Start Time: {blockData.startTime}</p>}
                        {blockData.endTime && <p>End Time: {blockData.endTime}</p>}
                        {blockData.sampleRate && <p>Sample rate: {blockData.sampleRate} Hz</p>}
                        {blockData.precision && (
                          <p>Audio precision: {block?.xRaw?.int8Data ? 8 : block?.precision} bits</p>
                        )}
                        <p>
                          X axis: {blockData.xRaw.length} samples | Y axis: {blockData.yRaw.length} samples | Z axis:{' '}
                          {blockData.zRaw.length} samples
                        </p>
                        {hasData ? (
                          <button
                            type="button"
                            onClick={() => handleDownloadWav(block, blockIndex)}
                            className="download-wav-button"
                          >
                            Download WAV File (Block {blockIndex + 1})
                          </button>
                        ) : (
                          <p className="no-accelerometer-data">No accelerometer raw data found in this block</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : accelData.xRaw.length > 0 || accelData.yRaw.length > 0 || accelData.zRaw.length > 0 ? (
                <div className="wav-download-section">
                  <h3>Accelerometer Data</h3>
                  <p>
                    X axis: {accelData.xRaw.length} samples | Y axis: {accelData.yRaw.length} samples | Z axis:{' '}
                    {accelData.zRaw.length} samples
                  </p>
                  {accelData.sampleRate && <p>Sample rate: {accelData.sampleRate} Hz</p>}
                  <button
                    type="button"
                    onClick={() => {
                      if (accelData.xRaw.length === 0 && accelData.yRaw.length === 0 && accelData.zRaw.length === 0) {
                        alert('No accelerometer raw data found in this TLV');
                        return;
                      }
                      const inputBitsPerSample = tlvJson?.accelerometerRaw?.xRaw?.int8Data
                        ? 8
                        : tlvJson?.accelerometerRaw?.precision;
                      const sampleRate = accelData.sampleRate || 44100;
                      const wavBlob = generateWavBlob(
                        accelData.xRaw,
                        accelData.yRaw,
                        accelData.zRaw,
                        sampleRate,
                        inputBitsPerSample,
                      );
                      const filename = `accelerometer_${device_data.id}_${Date.now()}.wav`;
                      downloadBlob(wavBlob, filename);
                    }}
                    className="download-wav-button"
                  >
                    Download WAV File
                  </button>
                </div>
              ) : (
                <p className="no-accelerometer-data">No accelerometer raw data found in this TLV</p>
              )}
              <MotorCurrentDebugSection tlvJson={tlvJson} />
              <hr />
            </div>
          );
        })}
      </>
    </div>
  );
}
